import { CommonUI } from "@src/common/CommonUI/CommonUI";
import { IllustrationBG } from "@src/common/IllustrationBG/IllustrationBG";
import { PageLayout } from "@src/common/PageLayout";
import { UTMNavigate } from "@src/common/UTMNavigate";
import { APP_ROUTES, IDLE_MODAL_STATES } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useIdleTimerContext } from "@src/context/IdleTimerContext";
import { useEntryRouteBootstrap } from "@src/hooks/useEntryRouteBootstrap";
import { useIsCombinedFindProviderTreatment } from "@src/routes/FindProvider/combined-find-provider-experiment";
import ClientLogsService from "@src/services/client-logs.service";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { ToastProvider } from "@uhg-abyss/web/ui/Toast";
import { Fragment, useEffect, useRef, useState } from "react";
import { GetStartedForm } from "./GetStartedForm";
import { GetStartedIntro } from "./GetStartedIntro";
import { NavButtons } from "./NavButtons";

const GetStartedContent = styled("div", {
  height: "auto !important",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "start",
  width: "100%",
});

const BACKGROUND_BOTTOM_PADDING = 80;

export const GetStarted = () => {
  const { data } = useApplicationContext();
  const { modalState } = useIdleTimerContext();
  const isFromReferFlow = data.isReferrerFlow ?? false;
  const isCombinedTreatment = useIsCombinedFindProviderTreatment();
  const shouldRedirectToCombinedFindProvider =
    isCombinedTreatment && !isFromReferFlow && modalState !== IDLE_MODAL_STATES.TIMEOUT;
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const contentRef = useRef<HTMLDivElement>(null);
  const [backgroundHeight, setBackgroundHeight] = useState(0);

  useEntryRouteBootstrap({
    initReady: shouldRedirectToCombinedFindProvider,
    isReferrerFlow: false,
    // FeatureManifestProvider established the UUID before it could assign the
    // treatment. Reuse that identity while preserving the attribution and
    // user-lead bootstrap that GetStartedForm normally owns.
    canEnsureUuid: false,
    digitalInfoLogLocation: "GetStartedCombinedTreatment",
  });

  useEffect(() => {
    if (shouldRedirectToCombinedFindProvider) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "GetStarted",
        "get_started_redirected_to_combined_find_provider",
        { acquisitionEntryRoute: "get_started" },
      );
    }
  }, [data.userUuid, shouldRedirectToCombinedFindProvider]);

  // Keep the blue background tall enough to contain the whole intro header.
  useEffect(() => {
    const content = contentRef.current;
    const topSection = content?.querySelector<HTMLElement>(
      '[data-testid="test-id-common-ui-top-section"]',
    );
    if (!content || !topSection || typeof ResizeObserver === "undefined") return;

    const update = () => {
      const bottom =
        topSection.getBoundingClientRect().bottom - content.getBoundingClientRect().top;
      setBackgroundHeight(Math.ceil(bottom + BACKGROUND_BOTTOM_PADDING));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(topSection);
    return () => observer.disconnect();
  }, []);

  if (shouldRedirectToCombinedFindProvider) {
    return <UTMNavigate to={APP_ROUTES.FIND_PROVIDER} replace />;
  }

  return (
    <Fragment>
      <ToastProvider />
      <PageLayout css={{ paddingBottom: isMobile ? "0" : "50px" }}>
        <IllustrationBG minHeight={backgroundHeight} />
        <GetStartedContent ref={contentRef} data-testid="test-id-get-started-content">
          <CommonUI
            metaTitle="Hope, help and healing start here"
            introContent={<GetStartedIntro />}
            FormComponent={GetStartedForm}
            topSectionMobileCss={{
              maxWidth: "100%",
              padding: "0 16px",
            }}
          />
          <div></div>
          <NavButtons isMobile={isMobile} />
        </GetStartedContent>
      </PageLayout>
    </Fragment>
  );
};
